import { tableAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';

const config = createMultiStyleConfigHelpers(tableAnatomy.keys);

const baseStyle = config.definePartsStyle({
  thead: {
    th: {
      border: 'none',
      borderRadius: 0,
      fontFamily: 'Instrument Sans',
      paddingX: 2,
      '&[data-is-numeric]': { textAlign: 'start' },
    },
  },
  tbody: {
    tr: {
      borderBottom: '1px solid',
      borderColor: 'whiteAlpha.200',
      _light: { borderColor: 'blackAlpha.200' },
    },
    td: {
      paddingY: 1,
      paddingX: 2,
      '&[data-is-numeric]': { textAlign: 'start' },
    },
  },
});

const entityDirectoryCompact = {
  table: {
    background: 'entityDirectory.surface',
    border: '1px solid',
    borderColor: 'entityDirectory.border',
    width: 'full',
  },
  thead: {
    background: 'entityDirectory.raisedSurface',
    borderBottom: '1px solid',
    borderColor: 'entityDirectory.border',
    th: {
      color: 'entityDirectory.mutedText',
      fontSize: '10px',
      fontWeight: 'bold',
      height: '44px',
      letterSpacing: '0.07em',
      overflow: 'hidden',
      paddingX: 4,
      textOverflow: 'ellipsis',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    },
  },
  tbody: {
    tr: {
      background: 'entityDirectory.surface',
      borderBottom: '1px solid',
      borderColor: 'entityDirectory.border',
      height: '66px',
      transition: 'background-color 120ms ease',
      _last: { borderBottom: 0 },
      _hover: { background: 'entityDirectory.raisedSurface' },
    },
    td: {
      color: 'entityDirectory.text',
      fontSize: '13px',
      paddingX: 4,
      paddingY: 2,
      verticalAlign: 'middle',
    },
  },
};

const variants = {
  stickyHeader: {
    thead: {
      backgroundColor: 'texas.bg.800',
      opacity: 0.95,
      pos: 'sticky',
      top: 0,
      zIndex: 1,
      borderBottom: 0,
      _light: {
        backgroundColor: 'gray.50',
        borderColor: 'gray.50',
      },
    },
  },
  salesTiers: {
    thead: {
      td: {
        padding: '0 0 1em 0',
        fontWeight: 'bold',
        verticalAlign: 'baseline',
      },
    },
    tbody: {
      tr: {
        borderBottom: '1px solid',
        borderBottomColor: 'gray.800',
        _hover: { background: 'whiteAlpha.100' },
        _light: {
          borderBottomColor: 'gray.50',
          _hover: { background: 'blackAlpha.100' },
        },
        td: { padding: '0.4em', transition: 'background-color 200ms ease' },
      },
    },
    tfoot: {
      td: {
        padding: '1em 0 0 0',
        fontWeight: 'bold',
        fontSize: '12px',
      },
    },
  },
  branchOverview: {
    table: { bg: 'texas.bg.blueTint.800', _light: { bg: 'white' } },
    thead: {
      border: 'none',
      bg: 'texas.bg.blueTint.800',
      _light: { bg: 'white' },
      tr: {
        color: 'white',
        borderBottom: '1px solid',
        borderBottomColor: 'texas.bg.blueTint.700',
        _hover: { bg: 'texas.bg.blueTint.800' },
        _light: { color: 'texas.bg.900', _hover: { bg: 'white' } },
      },
    },
    tbody: {
      tr: {
        borderBottom: '2px solid',
        borderBottomColor: 'texas.bg.blueTint.800',
        color: 'white',
        _hover: { background: 'whiteAlpha.100' },
        _light: {
          color: 'texas.bg.900',
          borderBottomColor: 'white',
          _hover: { background: 'blackAlpha.100' },
        },
      },
      td: { padding: '0.2em' },
      th: { padding: '0.2em' },
    },
  },
  configuration: {
    tbody: {
      td: { fontSize: '14px', verticalAlign: 'baseline' },
    },
  },
  entityDirectoryCompact,
  'entity-directory': {
    table: {
      minWidth: '1095px',
      tableLayout: 'fixed',
      width: 'full',
    },
    thead: {
      background: 'entityDirectory.raisedSurface',
      borderBottom: '1px solid',
      borderColor: 'entityDirectory.border',
      th: {
        color: 'entityDirectory.mutedText',
        fontSize: '10px',
        fontWeight: 'bold',
        height: '44px',
        letterSpacing: '0.07em',
        overflow: 'hidden',
        paddingX: 4,
        textOverflow: 'ellipsis',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      },
    },
    tbody: {
      tr: {
        height: '66px',
        borderBottom: '1px solid',
        borderColor: 'entityDirectory.border',
        _hover: {
          background: 'entityDirectory.raisedSurface',
        },
      },
      td: {
        color: 'entityDirectory.text',
        fontSize: '13px',
        paddingX: 4,
        paddingY: 2,
        verticalAlign: 'middle',
      },
    },
  },
  'shipment-overview': {
    table: {
      minWidth: '1120px',
      tableLayout: 'fixed',
      width: 'full',
    },
    thead: {
      background: 'entityDirectory.raisedSurface',
      borderBottom: '1px solid',
      borderColor: 'entityDirectory.border',
      th: {
        color: 'entityDirectory.mutedText',
        fontSize: '10px',
        fontWeight: 'bold',
        height: '44px',
        letterSpacing: '0.07em',
        overflow: 'hidden',
        paddingX: 3,
        textOverflow: 'ellipsis',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      },
    },
    tbody: {
      tr: {
        height: '66px',
        borderBottom: '1px solid',
        borderColor: 'entityDirectory.border',
        transition: 'background-color 120ms ease',
        _last: { borderBottom: 0 },
        _hover: { background: 'entityDirectory.raisedSurface' },
      },
      td: {
        color: 'entityDirectory.text',
        fontSize: '13px',
        paddingX: 3,
        paddingY: 2,
        verticalAlign: 'middle',
      },
    },
  },
  'inquiry-detail': {
    table: {
      minWidth: '760px',
      width: 'full',
    },
    thead: {
      background: 'inquiryOverview.raisedSurface',
      borderBottom: '1px solid',
      borderColor: 'inquiryOverview.border',
      th: {
        color: 'inquiryOverview.mutedText',
        fontSize: '11px',
        fontWeight: 'normal',
        height: '46px',
        letterSpacing: '0.075em',
        paddingX: 5,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      },
    },
    tbody: {
      tr: {
        height: '76px',
        borderBottom: '1px solid',
        borderColor: 'inquiryOverview.border',
        transition: 'background-color 120ms ease',
        _last: { borderBottom: 0 },
        _hover: { background: 'inquiryOverview.raisedSurface' },
      },
      td: {
        color: 'inquiryOverview.text',
        fontSize: '13px',
        paddingX: 5,
        paddingY: 3,
        verticalAlign: 'middle',
      },
    },
  },
  striped: {
    table: {
      size: 'sm',
      w: 'full',
      maxHeight: '380px',
      overflowY: 'auto',
      overflowX: 'hidden',
      colorScheme: 'gray',
    },
    thead: {
      pos: 'sticky',
      top: '0',
      zIndex: 1,
      background: 'texas.bg.800',
    },
    th: {
      color: 'white',
      px: '2',
    },
  },
  'select-product-approval': {
    table: { width: 'full' },
    thead: {
      border: 'none',
    },
    th: {
      p: '4px',
      fontSize: '14px',
    },
    td: { p: '4px', fontSize: '14px' },
    tbody: {
      tr: {
        _hover: {
          backgroundColor: 'texas.bg.700',
          _light: { backgroundColor: 'gray.50' },
        },
      },
      td: { verticalAlign: 'baseline' },
    },
  },
  samples: {
    table: {
      borderCollapse: 'separate',
      borderSpacing: '0 4px',
    },
    thead: {
      border: 'none',
    },
    tbody: {
      tr: {
        border: 'none',
      },
      td: {
        borderRadius: '0',
        py: 4,
        bg: 'texas.bg.800',
        _light: {
          bg: 'gray.50',
        },
      },
      'td:first-of-type': {
        borderLeftRadius: 'md',
      },
      'td:last-of-type': {
        borderRightRadius: 'md',
      },
    },
  },
  'versions-list-view': {
    table: {
      borderCollapse: 'separate',
      borderSpacing: '0 4px',
    },
    thead: {
      th: {
        alignContent: 'end',
      },
      'th.material-header': {
        bg: 'texas.bg.800',
        _light: { bg: 'gray.50' },
      },
      'th.material-header-last': {
        bg: 'texas.bg.800',
        borderRight: '4px solid !important',
        borderRightColor: 'texas.bg.dark !important',
        _light: {
          borderRightColor: 'white !important',
          bg: 'gray.50',
        },
      },
    },
    tbody: {
      'tr.unique td:first-of-type': {
        pl: '7px',
        borderLeft: '3px solid',
        borderColor: 'gray.200',
        _light: {
          borderColor: 'gray.400',
        },
      },
      td: {
        bg: 'texas.bg.800',
        fontSize: '14px',
        color: 'white',
        borderRadius: '0',
        py: 2,
        _light: {
          bg: 'gray.50',
          color: 'texas.bg.950',
        },
      },
      'td:first-of-type': {
        borderLeftRadius: 'md',
        pl: '10px',
      },
      'td:last-of-type': {
        borderRightRadius: 'md',
      },
      'td:nth-of-type(n + 5)': {
        bg: 'texas.bg.900',
        alignContent: 'start',
        _light: {
          bg: 'gray.100',
        },
      },
    },
  },
};

export default config.defineMultiStyleConfig({ baseStyle, variants });
